import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
  OutputParametricSelector,
  ParametricSelector,
  Selector,
} from "reselect";
import { BettingState, COMBINABLE_LEG_TYPES_LIST, updateStake } from "@ppb/betslip-core";
import type {
  SportsbookBettingState,
  SportsbookBettingRunner,
  RunnersMetadata,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import type { Sport } from "@ppb/tbd-store/state/entities";
import {
  getSportsbookBettingState,
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "./betting-resolver";
import {
  processAllUniqueRunnersFailures,
  processAllUniqueCombinationsFailures,
  isMultiple,
  isSingle,
  isBetBuilder,
  groupCombinationsByMarketId,
  CastGroup,
  getUniqueFailedSGMCombinationGroups,
  isMultiBetBuilder,
  generateCastRunnersIds,
  generateCastGroupIds,
  isSingleLike,
  isBoostedMultiple,
  hasAnyInvalidCombinationFailure,
  groupLotteryCombinations,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import { createShallowEqualSelector } from "@ppb/tbd-store/helpers/selectors";
import { EventType } from "../../../constants";
import { sumWalletsAmounts } from "../../../helpers/generosity-wallets";
import type { BetslipApplicationState } from "../BetslipApplicationState";
import { getBetslipGroup } from "../betslip-module/betslip-module-selectors";
import { getSportsbookBetslipCapabilities } from "../capabilities/capabilities-selectors";
import { getSportsbookBetslipSession } from "../session/session-selectors";

export type QuickBetslipFallbackReason = "non_football_or_horse_racing_single" | "invalid_combination";

export type QuickBetslipBet =
  | { status: "valid"; combinationId: string }
  | { status: "pending" }
  | { status: "fallback"; reason: QuickBetslipFallbackReason };

const getBetslipRunnersMetadata = (state: BetslipApplicationState): RunnersMetadata =>
  getBettingResolvers(getBetslipGroup(state)).getMetadata(state);

export const getSportsbookBettingRunners = (state: BetslipApplicationState): BettingState.RunnersMap =>
  state.betting.sportsbookBetting.runners;

export const getSportsbookBettingReviewLines = (state: BetslipApplicationState): BettingState.LinesMap =>
  state.betting.sportsbookBetting.reviews.lines;

const getSportsbookBettingReviewCombinations = (state: BetslipApplicationState): BettingState.CombinationReviewMap =>
  state.betting.sportsbookBetting.reviews.combinations;

export const getSportsbookBettingImplyRunnerFailures = (
  state: BetslipApplicationState,
): BettingState.ImplyRunnerFailuresMap => state.betting.sportsbookBetting.failures.imply.runners;

export const getSportsbookBettingNumRunners = (state: BetslipApplicationState): number =>
  Object.keys(state.betting.sportsbookBetting.runners).length;

const getSportsbookBettingImplyLegFailures = (state: BetslipApplicationState): BettingState.ImplyLegFailuresMap =>
  state.betting.sportsbookBetting.failures.imply.legs;

const areWalletsEqual = (prevWallets: BettingState.WalletsMap, newWallets: BettingState.WalletsMap) =>
  prevWallets === newWallets ||
  (JSON.stringify(Object.keys(prevWallets).sort()) === JSON.stringify(Object.keys(newWallets).sort()) &&
    Object.values(prevWallets).every(
      (wallet) => JSON.stringify(wallet) === JSON.stringify(newWallets[wallet.walletId]),
    ));

const isSelectedWalletsAmountsContextEqual = (prevState: BettingState.Group, newState: BettingState.Group) =>
  areWalletsEqual(prevState?.wallets, newState?.wallets);

const getCombinationSelectedBonusWallets = (
  combinationId: string,
  wallets: BettingState.WalletsMap,
): BettingState.Wallet[] =>
  Object.values(wallets).filter(({ combinationId: walletCombinationId }) => combinationId === walletCombinationId);

export const createGetCombinationsSelectedWalletsAmounts = () =>
  createSelectorCreator(defaultMemoize, isSelectedWalletsAmountsContextEqual)(
    [(sportsbookBetting: BettingState.Group) => sportsbookBetting],
    ({ wallets, combinations }) =>
      Object.values(combinations).reduce<{
        [combinationId: string]: Pick<BettingState.Wallet, "combinationAmountPerLine" | "combinationAmount"> &
          Pick<BettingState.Combination, "numLines">;
      }>((acc, combination) => {
        const { id, numLines, isEachWaySelected } = combination;
        const selectedWallets = getCombinationSelectedBonusWallets(id, wallets);
        if (!selectedWallets.length) {
          return acc;
        }
        const walletsAmounts = selectedWallets.reduce(
          (
            { combinationAmountPerLine: accAmountPerLine, combinationAmount: accAmount },
            { combinationAmountPerLine, combinationAmount },
          ) => ({
            combinationAmountPerLine: sumWalletsAmounts(accAmountPerLine, combinationAmountPerLine),
            combinationAmount: sumWalletsAmounts(accAmount, combinationAmount),
          }),
          {
            combinationAmountPerLine: 0,
            combinationAmount: 0,
          },
        );
        acc[id] = {
          ...walletsAmounts,
          numLines: isEachWaySelected ? numLines * 2 : numLines,
        };
        return acc;
      }, {}),
  );

export const createCombinationCounterSelector = (): OutputParametricSelector<
  BetslipApplicationState,
  string,
  number,
  (combinations: BettingState.CombinationsMap, combinationId: string) => number
> => {
  return createSelector(
    [getSportsbookBettingCombinations, (_: BetslipApplicationState, combinationId: string) => combinationId],
    (combinations, combinationId): number => {
      const combination = combinations[combinationId];

      if (!combination) {
        return 0;
      }

      return combination.legs.length;
    },
  );
};

type CombinationCalculationsValues = {
  combinationId: string;
  stake?: number;
};
export const createGetCalculatedCombination = (): OutputParametricSelector<
  BetslipApplicationState,
  CombinationCalculationsValues,
  BettingState.Combination | undefined,
  (betting: SportsbookBettingState, values: CombinationCalculationsValues) => BettingState.Combination | undefined
> =>
  createSelector(
    [getSportsbookBettingState, (_: BetslipApplicationState, values: CombinationCalculationsValues) => values],
    (betting, values) => {
      const { combinationId, stake } = values;

      if (!combinationId || !betting.combinations[combinationId]) {
        return undefined;
      }

      const calculatedGroup = updateStake(betting, { combinationId, stake });

      return calculatedGroup.combinations[combinationId];
    },
  );

type PickGreatestOddCombinationOptions = {
  isSingleSelectionOnly?: boolean;
  allowMissingOdds?: boolean;
};

const isEligibleGreatestOddCombination = (
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
  options?: PickGreatestOddCombinationOptions,
): boolean => {
  const legCount = Object.keys(legs ?? {}).length;
  const isSingleLegCombination = isSingle(combination, legs);
  const isMultiLegCombination =
    isMultiple(combination) ||
    isBetBuilder(combination) ||
    isMultiBetBuilder(combination) ||
    isBoostedMultiple(combination);

  const matchesQuickBetslipLegCount =
    !options?.isSingleSelectionOnly ||
    (legCount <= 1 ? isSingleLegCombination || isBetBuilder(combination) : isMultiLegCombination);

  return (
    (options?.allowMissingOdds || !!combination.odds) &&
    combination.numLines === 1 &&
    (isSingleLegCombination || isMultiLegCombination) &&
    matchesQuickBetslipLegCount
  );
};

const pickGreatestOddCombination = (
  combinations: BettingState.CombinationsMap,
  legs: BettingState.LegsMap,
  options?: PickGreatestOddCombinationOptions,
): BettingState.Combination | undefined => {
  const [greatestCombination] = Object.values(combinations)
    .filter((combination: BettingState.Combination) => isEligibleGreatestOddCombination(combination, legs, options))
    .sort((combinationA, combinationB) => {
      if (!combinationA.odds || !combinationB.odds) {
        return 0;
      }

      return combinationB.odds.decimalOdds - combinationA.odds.decimalOdds;
    });

  return greatestCombination;
};

export const pickQuickBetslipCombination = (
  combinations: BettingState.CombinationsMap,
  legs: BettingState.LegsMap,
): BettingState.Combination | undefined =>
  pickGreatestOddCombination(combinations, legs, { isSingleSelectionOnly: true }) ??
  pickGreatestOddCombination(combinations, legs, { isSingleSelectionOnly: true, allowMissingOdds: true });

export const createGetGreatestOddCombinationSelector = (): Selector<
  BetslipApplicationState,
  BettingState.Combination | undefined
> =>
  createSelector([getSportsbookBettingCombinations, getSportsbookBettingLegs], (combinations, legs) =>
    pickGreatestOddCombination(combinations, legs),
  );

export const createHasMultiplesSelector = () =>
  createSelector([getSportsbookBettingCombinations], (combinations): boolean =>
    Object.values(combinations).some((combination) => isMultiple(combination)),
  );

const getPlaceRunnersFailures = (state: BetslipApplicationState): BettingState.PlaceRunnerFailuresMap =>
  state.betting.sportsbookBetting.failures.place.runners;

const getPlaceCombinationsFailures = (state: BetslipApplicationState): BettingState.PlaceCombinationFailuresMap =>
  state.betting.sportsbookBetting.failures.place.combinations;

export const getOperationalFailure = (state: BetslipApplicationState): BettingState.PlaceOperationFailure | undefined =>
  state.betting.sportsbookBetting.failures.place.operational;

export const getAllUniqueRunnersFailures = createSelector([getPlaceRunnersFailures], processAllUniqueRunnersFailures);

export const getAllUniqueCombinationsFailures = createSelector(
  [getPlaceCombinationsFailures],
  processAllUniqueCombinationsFailures,
);

export const getSportsbookBettingValidations = (state: BetslipApplicationState): BettingState.GroupValidationsResult =>
  state.betting.sportsbookBetting.validations;

const createSingleCombinationIds = createSelector(
  [getSportsbookBettingLegs, getSportsbookBettingCombinations],
  (legs, combinations) =>
    Object.values(combinations)
      .filter((combination) => isSingleLike(combination, legs))
      .sort((combinationA, combinationB) => combinationA.creationTimestamp - combinationB.creationTimestamp)
      .map(({ id }) => id),
);

export const createGetSelectionIdsSelector = () =>
  createShallowEqualSelector(
    (state: BettingState.LegsMap): string[] =>
      Object.values(state)
        .filter(({ legType }) => COMBINABLE_LEG_TYPES_LIST.includes(legType))
        .map(({ id }) => id),
    (legIds: string[]) => legIds,
  );

export const createGetReviewCombinationLineIdsSelector = (): ParametricSelector<
  BetslipApplicationState,
  string,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [getSportsbookBettingReviewCombinations, (_: BetslipApplicationState, id: string) => id],
      (combinations, id) => combinations[id]?.lineIds || [],
    ),
    (lineIds: string[]) => lineIds,
  );

export const getSingleCombinationIds = createShallowEqualSelector(
  createSingleCombinationIds,
  (combinationIds: string[]) => combinationIds,
);

export const createGetBetBuilderCombinationIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    createSelector([getSportsbookBettingCombinations], (combinations) =>
      Object.values(combinations)
        .filter((combination) => isBetBuilder(combination))
        .map(({ id }) => id),
    ),
    (combinationIds: string[]) => combinationIds,
  );

export const createGetBoostedCombinationsSelector = (): Selector<BetslipApplicationState, BettingState.Combination[]> =>
  createSelector([getSportsbookBettingCombinations], (combinations) =>
    Object.values(combinations).filter((combination) => combination.isBoosted),
  );

export const createGetCastRunnerIdsSelector = (): ParametricSelector<
  BetslipApplicationState,
  string | undefined,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [
        getSportsbookBettingCombinations,
        getSportsbookBettingLegs,
        getSportsbookBettingRunners,
        (_: BetslipApplicationState, combinationId?: string) => combinationId,
      ],
      generateCastRunnersIds,
    ),
    (runnerIds: string[]) => runnerIds,
  );

export const createGetFailureLegIdsByCombinationGroupIdSelector = (): ParametricSelector<
  BetslipApplicationState,
  number | null,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [
        getSportsbookBettingLegs,
        getSportsbookBettingImplyRunnerFailures,
        (_: BetslipApplicationState, combinationGroup: number | null) => combinationGroup,
      ],
      (legs, failures, combinationGroup) => {
        if (combinationGroup) {
          const runnersOfCombinationGroup = Object.keys(failures).filter((failedRunnerId) => {
            const runnerFailures = failures[failedRunnerId];

            return runnerFailures.some(({ combinationGroups }) => combinationGroups.includes(combinationGroup));
          });

          return Object.keys(legs).filter((legId) =>
            legs[legId].runners.some((runnerId) => runnersOfCombinationGroup.includes(runnerId)),
          );
        }

        return [];
      },
    ),
    (legIds: string[]) => legIds,
  );

export const createGetLegIdsByCombinationGroupIdSelector = (): ParametricSelector<
  BetslipApplicationState,
  string | null,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [getSportsbookBettingLegs, (_: BetslipApplicationState, combinationGroupId: string | null) => combinationGroupId],
      (legs, combinationGroupId) => {
        if (combinationGroupId) {
          return Object.keys(legs).filter((legId) => legs[legId].groupId === combinationGroupId);
        }

        return [];
      },
    ),
    (legIds: string[]) => legIds,
  );

export const createGetBoostedUncombinedGroupIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    createSelector(
      [getSportsbookBettingLegs, getSportsbookBettingImplyLegFailures, getSportsbookBettingImplyRunnerFailures],
      (legs, legFailures, runnerFailures) => {
        const failedBoostGroupIds = Object.values(legs)
          .filter((leg) => {
            if (!leg.isBoosted || !leg.groupId) {
              return false;
            }

            const isBoostedLegFailing = !!legFailures[leg.id];
            const hasFailingRunners = leg.runners.some((runnerId) => !!runnerFailures[runnerId]);

            return isBoostedLegFailing || hasFailingRunners;
          })
          .map((leg) => leg.groupId as string); // Only boosted legs with groupId will be present in this map

        return Array.from(new Set(failedBoostGroupIds));
      },
    ),
    (combinationGroupIds: string[]) => combinationGroupIds,
  );

export const createCombinationGroupFailuresSelector = (): Selector<BetslipApplicationState, number[]> =>
  createShallowEqualSelector(
    createSelector(
      [getSportsbookBettingImplyRunnerFailures, getSportsbookBettingCombinations],
      (failures, combinations) => {
        const allUniqueFailedCombinationGroups = getUniqueFailedSGMCombinationGroups(failures);
        const allValidCombinationGroups = Object.values(combinations).map(({ combinationGroup }) => combinationGroup);

        return allUniqueFailedCombinationGroups.filter(
          (failedCombinationGroup) => !allValidCombinationGroups.includes(failedCombinationGroup),
        );
      },
    ),
    (combinationGroups: number[]) => combinationGroups,
  );

export const createGetRunnerByCombinationGroupSelector = (): ParametricSelector<
  BetslipApplicationState,
  number,
  string[]
> =>
  createShallowEqualSelector(
    createSelector(
      [
        getSportsbookBettingImplyRunnerFailures,
        (_: BetslipApplicationState, combinationGroup: number) => combinationGroup,
      ],
      (failures, combinationGroup) =>
        Object.keys(failures).filter((failedRunnerId) => {
          const runnerFailures = failures[failedRunnerId];

          return !!runnerFailures.some(({ combinationGroups }) => combinationGroups.includes(combinationGroup));
        }),
    ),
    (runnerIds: string[]) => runnerIds,
  );

export const createGetCastGroupIdsSelector = (): Selector<BetslipApplicationState, string[]> =>
  createShallowEqualSelector(
    createSelector([getSportsbookBettingState, getBetslipRunnersMetadata], (bettingState, bettingMetadata) =>
      generateCastGroupIds(groupCombinationsByMarketId(bettingState), bettingMetadata),
    ),
    (combinationIds: string[]) => combinationIds,
  );

export const createGetCastGroupSelector = (): OutputParametricSelector<
  BetslipApplicationState,
  string,
  CastGroup,
  (bettingState: SportsbookBettingState, castGroupId: string) => CastGroup
> =>
  createSelector(
    [getSportsbookBettingState, (_: BetslipApplicationState, castGroupId: string) => castGroupId],
    (bettingState, castGroupId) => {
      const castGroups = groupCombinationsByMarketId(bettingState);

      return castGroups[castGroupId];
    },
  );

export const createGetLotteryGroupsSelector = () =>
  createSelector([getSportsbookBettingState, getBetslipRunnersMetadata], (bettingState, metadata) =>
    groupLotteryCombinations(bettingState, metadata),
  );

export const createSportsbookBettingRunnerSelector = (): ParametricSelector<
  BettingState.RunnersMap,
  string,
  SportsbookBettingRunner
> =>
  createSelector(
    (sportsbookBettingRunnersState: BettingState.RunnersMap, runnerId: string) =>
      sportsbookBettingRunnersState[runnerId],
    (runner) => runner,
  );

export const getBoostedCombination = (
  state: BetslipApplicationState,
  id: string,
): BettingState.Combination | undefined =>
  Object.values(state.betting.sportsbookBetting.combinations).find(
    (combination) => combination.isBoosted && combination.id === id,
  );

export const createQuickBetslipBetPickerSelector = () => {
  const isLoggedIn = (state: BetslipApplicationState) => getSportsbookBetslipSession(state).loggedIn;
  const isQuickBetslipEnabled = (state: BetslipApplicationState) =>
    getSportsbookBetslipCapabilities(state).quickBetslip.enabled;
  const isEventType = (sportId: RunnersMetadata[string]["sportId"] | Sport["sportId"] | string, eventType: EventType) =>
    Number(sportId) === eventType;

  return createSelector(
    [
      isLoggedIn,
      getBetslipRunnersMetadata,
      getSportsbookBettingCombinations,
      getSportsbookBettingImplyRunnerFailures,
      getSportsbookBettingLegs,
      isQuickBetslipEnabled,
    ],
    (isLoggedIn, metadata, combinations, runnerFailures, legs, isQuickBetslipEnabled): QuickBetslipBet | null => {
      if (!isQuickBetslipEnabled) {
        return null;
      }

      if (!isLoggedIn) {
        return null;
      }

      const runnersMetadata = Object.values(metadata);
      const hasLegs = Object.keys(legs ?? {}).length > 0;
      const areAllLegsFromFootballSport =
        runnersMetadata.length > 0 &&
        runnersMetadata.every((runnerMetadata) => isEventType(runnerMetadata.sportId, EventType.FOOTBALL));

      const isSingleLegHorseRacingSelection =
        Object.keys(legs ?? {}).length === 1 &&
        runnersMetadata.length === 1 &&
        isEventType(runnersMetadata[0].sportId, EventType.HORSE_RACING);

      const isQuickBetslipEligible = areAllLegsFromFootballSport || isSingleLegHorseRacingSelection;

      if (!isQuickBetslipEligible) {
        if (runnersMetadata.length > 0) {
          return { status: "fallback", reason: "non_football_or_horse_racing_single" };
        }
        // Keep QBS mounted while runner metadata catches up after selection changes.
        if (hasLegs) {
          return { status: "pending" };
        }
        return null;
      }

      const hasInvalidCombinationFailure = Object.values(runnerFailures).some((failures) =>
        hasAnyInvalidCombinationFailure(failures),
      );

      if (hasInvalidCombinationFailure) {
        return { status: "fallback", reason: "invalid_combination" };
      }

      const combination = pickQuickBetslipCombination(combinations, legs);

      if (!combination) {
        return { status: "pending" };
      }

      const { id } = combination;

      return {
        status: "valid",
        combinationId: id,
      };
    },
  );
};

export const createSinglesOnTopSelector = (): Selector<BetslipApplicationState, boolean> => {
  const isSinglesOnTopEnabled = (state: BetslipApplicationState) =>
    getSportsbookBetslipCapabilities(state).singlesOnTop.enabled;

  return createSelector(
    [getBetslipRunnersMetadata, isSinglesOnTopEnabled],
    (metadata, isSinglesOnTopEnabled): boolean => {
      if (!isSinglesOnTopEnabled) {
        return false;
      }

      const runnersMetadata = Object.values(metadata);

      return (
        runnersMetadata.length > 0 &&
        runnersMetadata.every((runnerMetadata) => runnerMetadata.sportId === EventType.HORSE_RACING)
      );
    },
  );
};
